<script lang="ts">
    import {
        Block,
        ListInput,
        Button,
        List,
        ListItem,
        Link,
        Popup,
        Page,
        Navbar,
        NavRight,
        ListGroup,
        Icon,
        PageContent,
        Progressbar,
        Popover, f7
    } from 'framework7-svelte';
    import { tick } from 'svelte';
    import { _ } from 'svelte-i18n';
    
    import { makeApiCall } from '../api/client';
    import { notify } from '../utils/notify';
    import { indicators } from '../utils/indicators';
    import InsightTable from './InsightTable.svelte';
    import InsightMap from './InsightMap.svelte';
    import MeasurementIntervalPopup from './MeasurementIntervalPopup.svelte';
    import InsightGraphPopup from './InsightGraphPopup.svelte';
    import { PERMISSION_CONFIG_INSIGHT, PERMISSION_USE_INSIGHT, PERMISSION_VIEW_INSIGHT } from '../utils/permissions';
    import FloatingButtonContainer from './FloatingButtonContainer.svelte';
    import TimestampSelector from './menu-buttons/TimestampSelector.svelte';
    import IndicatorSelector from './menu-buttons/IndicatorSelector.svelte';
    import PointMeasurementSelector from './menu-buttons/PointMeasurementSelector.svelte';
    import HereTrafficOptionSelector from './menu-buttons/HereTrafficOptionSelector.svelte';
    import MapboxTrafficOptionSelector from './menu-buttons/MapboxTrafficOptionSelector.svelte';
    import RouteSelector from './menu-buttons/RouteSelector.svelte';
    import RouteManager from './route-management/RouteManager.svelte';
    import {
        ROUTE_MEASUREMENT_TABLE_PAGE_TITLE,
        SECTION_MEASUREMENT_TABLE_PAGE_TITLE
    } from "../constants/measurement-table-constants";
    import {projectStore} from "../stores/project.store";
    import {sectionMeasurementsMappedStore} from "../stores/section-measurements-mapped.store";
    import {routeMeasurementsMappedStore} from "../stores/route-measurements-mapped.store";
    import type {MeasurementSettingsRequest} from "../types/requests/measurement-settings-request.type";
    import {
        getProjectFlowMeasurementsExport,
        putProjectMap,
        updateProjectMeasurementSettings
    } from "../api/projects.api";
    import {parseProjectMapRequestFromCsv} from "../helpers/project-map-import.helper";
    import {measurementMomentsStore} from "../stores/measurement-moments.store";
    import {MeasurementTableType} from "../types/measurements/measurement-table.type";

    $: id = `project-${$projectStore.data?.id}`;
    
    export let setSelectedMeasurementMoment: (id: string) => void | Promise<void>;
    export let refreshProject: () => void | Promise<void>;
    export let refreshMeasurements: () => void | Promise<void>;
    
    let importPopupOpened = false;
    let routeManagementSectionOpened = false;
    let tableSectionOpened = false;
    let tableRouteOpened = false;
    let graphRouteOpened = false;
    let importPopupClose: () => void | undefined;
    let importCsvContents = '';
    
    let showSpeedValues: boolean = false;
    let showNdwSpeed: boolean = false;
    let showNdwFlow: boolean = false;
    let modeNdwActive: boolean = false;
    let modeNdwInactive: boolean = false;

    let exportFlowFlowGateIds = '';
    let exportFlowInProgress = false;

    let indicator = '';
    let trafficFlowMode: 'none' | 'coverage' | 'speedhq' | 'speedlq' | 'congestionhq' | 'congestionlq' = 'none';

    $: indicatorIdx = indicators.findIndex((x) => x.id === indicator);
    $: measurementsExist = $measurementMomentsStore.data?.length > 0;
    $: routesExist = $projectStore.data?.routes?.length > 0;
    
    let congestionProfile: string[] = [];

    function selectIndicator(event) {
        indicator = event.detail;
        repaint();
    }

    function setShowValues(event) {
        showSpeedValues = event.detail;
        repaint();
    }

    function selectTrafficFlowMode(event) {
        trafficFlowMode = event.detail;
        repaint();
    }

    function repaint() {
        tick();
    }

    function updateCsvContents(e: CustomEvent) {
        const value = (e.target as any).value;
        importCsvContents = value;
    }

    function canImport(csv: string) {
        return csv.includes(':');
    }

    async function doImport() {
        const request = parseProjectMapRequestFromCsv(importCsvContents);
        if (request.routes.length === 0) {
            notify($_('pages.project.CSV_IMPORT_FAILED'));    
            return;
        }

        await putProjectMap($projectStore.data.id, request);

        // Setting importPopupOpened to false does not work, so do it via the popup itself
        importPopupClose?.();
        notify($_('pages.project.CSV_IMPORT_SUCCESS', { values: { numberOfRoutes: request.routes.length, numberOfGates: request.gates.length }}));
        
        setTimeout(async () => {
            await refreshProject()
        }, 1000)
    }

    async function selectTimestamp(event) {
        await setSelectedMeasurementMoment(event.detail)
    }

    async function doMeasurement() {
        await makeApiCall(`api/projects/${$projectStore.data.id}/perform-measurements`, 'GET');

        setTimeout(async () => {
            await refreshMeasurements()
        }, 7000)
    }

    async function changeMeasurementConfig(value: MeasurementSettingsRequest) {
        await updateProjectMeasurementSettings(value);
        notify($_('pages.project.MEASUREMENTS_SETTING_SAVED'));
        await refreshProject();
    }
    
    async function exportFlow() {
        const blob = await getProjectFlowMeasurementsExport($projectStore.data.id);

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = 'measurements.csv';
        link.click();

        URL.revokeObjectURL(url);
        f7.popup.close('#popup-export-flow');
    }
</script>

<Page pageContent={false} {id}>
    <Popover class="menu">
        <List ul={false}>
            <ListGroup>
                <ListItem href="/" popoverClose>Menu</ListItem>
            </ListGroup>
            {#if $PERMISSION_CONFIG_INSIGHT}
                <ListGroup>
                    <ListItem title="{$_('shared.IMPORT')}" groupTitle></ListItem>
                    <ListItem link="#" title="{$_('pages.project.IMPORT_ROUTES')}" popupOpen="#import-popup" popoverClose>
                        <Icon slot="media" material="upload" />
                    </ListItem>
                </ListGroup>
            {/if}
            {#if $PERMISSION_VIEW_INSIGHT && measurementsExist}
                <ListGroup>
                    <ListItem title="{$_('pages.project.INFORMATION_INSIGHTS')}" groupTitle></ListItem>
                    <ListItem link="#" title="{$_('pages.project.SECTION_TABLE')}" popupOpen="#table-section" popoverClose>
                        <Icon slot="media" material="table_chart" />
                    </ListItem>
                    <ListItem link="#" title="{$_('pages.project.ROUTE_TABLE')}" popupOpen="#table-route" popoverClose>
                        <Icon slot="media" material="table_chart" />
                    </ListItem>
                    <ListItem link="#" title="{$_('pages.project.ROUTE_GRAPH')}" popupOpen="#graph-route" popoverClose>
                        <Icon slot="media" material="show_chart" />
                    </ListItem>
                    <ListItem link="#" title="{$_('pages.project.EXPORT_FLOW_MEASUREMENTS')}" popupOpen="#popup-export-flow" popoverClose>
                        <Icon slot="media" material="save_as" />
                    </ListItem>
                </ListGroup>
            {/if}
            <ListGroup>
                <ListItem title="{$_('pages.project.CONTROLS')}" groupTitle></ListItem>
                {#if $PERMISSION_CONFIG_INSIGHT}
                    <ListItem link="#" title="{$_('pages.route-editor.ROUTE_MANAGEMENT')}" popupOpen="#route-management-section" popoverClose>
                        <Icon slot="media" material="table_chart" />
                    </ListItem>
                {/if}
                {#if $PERMISSION_USE_INSIGHT && routesExist}
                    <ListItem link="#" title="{$_('pages.project.PERFORM_MEASUREMENT')}" on:click={doMeasurement} popoverClose>
                        <Icon slot="media" material="commit" />
                    </ListItem>
                {/if}
                <ListItem link="#" title="{$_('shared.REFRESH')}" on:click={() => refreshMeasurements()} popoverClose>
                    <Icon slot="media" material="refresh" />
                </ListItem>
                {#if $PERMISSION_CONFIG_INSIGHT}
                    <ListItem link="#" title="{$_('pages.project.MEASUREMENT_SETTINGS')}" popupOpen="#measurement-interval-popup" popoverClose>
                        <Icon slot="media" material="straighten" />
                    </ListItem>
                {/if}
            </ListGroup>
        </List>
    </Popover>

    <PageContent>
        <InsightMap
            {trafficFlowMode}
            {indicatorIdx}
            {congestionProfile}
            {showSpeedValues}
            {showNdwFlow}
            {showNdwSpeed}
            {modeNdwActive}
            {modeNdwInactive}
        />

        <FloatingButtonContainer>
            <TimestampSelector on:timestampSelected={selectTimestamp} />

            <IndicatorSelector on:indicatorSelected={selectIndicator} on:showValuesChanged={setShowValues} />

            <PointMeasurementSelector bind:modeNdwActive bind:modeNdwInactive bind:showNdwSpeed bind:showNdwFlow />

            <HereTrafficOptionSelector on:trafficFlowModeSelected={selectTrafficFlowMode} />

            <MapboxTrafficOptionSelector bind:congestionProfile />

            <RouteSelector />
            
            <svelte:fragment slot="right">
                {#if measurementsExist}
                <div>
                    <Link class="btn btn-graph" popupOpen="#graph-route" iconOnly iconMaterial="bid_landscape" />
                </div>
                {/if}
                <div>
                    <Link class="btn btn-menu" popoverOpen=".menu" iconOnly iconMaterial="menu" />
                </div>
            </svelte:fragment>
        </FloatingButtonContainer>
    </PageContent>
</Page>

<Popup
    id="import-popup"
    opened={importPopupOpened}
    onPopupOpened={(popup) => (importPopupClose = () => popup.close())}
    onPopupClosed={() => (importPopupOpened = false)}
>
    <Page>
        <Navbar title="CSV Import">
            <NavRight>
                <Link popupClose>Sluiten</Link>
            </NavRight>
        </Navbar>
        <Block>
            <List noHairlinesMd>
                <ListInput label="CSV" type="textarea" placeholder="CSV" resizable value={importCsvContents} onInput={updateCsvContents}></ListInput>

                <ListItem>
                    <Button disabled={!canImport(importCsvContents)} fill on:click={doImport}>
                        {$_('shared.IMPORT')}
                    </Button>
                </ListItem>
            </List>
        </Block>
    </Page>
</Popup>

{#if $PERMISSION_VIEW_INSIGHT}
    <Popup
        id="route-management-section"
        bind:opened={routeManagementSectionOpened}
        onPopupClosed={() => (routeManagementSectionOpened = false)}
        tabletFullscreen={true}
    >
        {#if routeManagementSectionOpened}
            <RouteManager
                on:insightUpdated={refreshProject} 
            />
        {/if}
    </Popup>

    <Popup id="table-section" bind:opened={tableSectionOpened} onPopupClosed={() => (tableSectionOpened = false)} tabletFullscreen={true}>
        {#if tableSectionOpened}
            <InsightTable
                tableType={MeasurementTableType.Section}
                allMeasurements={$sectionMeasurementsMappedStore}
            />
        {/if}
    </Popup>

    <Popup id="table-route" bind:opened={tableRouteOpened} onPopupClosed={() => (tableRouteOpened = false)} tabletFullscreen={true}>
        {#if tableRouteOpened}
            <InsightTable
                tableType={MeasurementTableType.Route}
                allMeasurements={$routeMeasurementsMappedStore}
            />
        {/if}
    </Popup>

    <Popup id="graph-route" bind:opened={graphRouteOpened} tabletFullscreen={true}>
        {#if graphRouteOpened}
            <InsightGraphPopup/>
        {/if}
    </Popup>

    <Popup id="popup-export-flow">
        {#if exportFlowInProgress}
            <Progressbar id="exportFlowProgressBar"></Progressbar>
        {/if}
        <List>
            <ListInput label="{$_('pages.dashboard.MEASUREMENT_POINTS')}" type="textarea" placeholder="{$_('pages.dashboard.MEASUREMENT_POINTS_COMMA_SEPERATED')}" resizable bind:value={exportFlowFlowGateIds}></ListInput>
            <ListItem>
                <Button on:click={exportFlow}>{$_('shared.DOWNLOAD')}</Button>
            </ListItem> 
        </List>
    </Popup>
{/if}

{#if $PERMISSION_CONFIG_INSIGHT}
    <Popup id="measurement-interval-popup">
        <MeasurementIntervalPopup
            on:changed={(ev) => changeMeasurementConfig(ev.detail)}
        ></MeasurementIntervalPopup>
    </Popup>
{/if}

<style lang="scss">
    @import '../assets/scss/colors';

    :global(.btn) {
        border-radius: 100px;
        padding: 8px;
        i {
            width: 24px;
            height: 24px;
        }
    }
    :global(.btn-graph) {
        border-radius: 100px;
        background: var(--Buko-sys-light-secondary-container, #ffdad6);
        color: #{$secondary-fixed-on};
    }

    :global(.btn-menu) {
        color: #{$surface-on-variant};
    }
</style>
